import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown } from "lucide-react";

const inp = "h-11 rounded-lg border border-[#E6E1D7] bg-white px-3 text-sm";

export const taxName = (list, id) => (list || []).find((x) => x.id === id)?.name;

export function groupRecipes(items, tax, by = "recipe_categories") {
  const field = by === "menu_categories" ? "menu_category_id" : by === "types" ? "type_id" : "recipe_category_id";
  const groups = (tax?.[by] || []).filter((c) => !c.parent_id).map((c) => ({ id: c.id, name: c.name, archived: c.archived, items: [] }));
  const map = Object.fromEntries(groups.map((g) => [g.id, g]));
  const unc = { id: "uncategorized", name: "Uncategorized", items: [] };
  items.forEach((r) => (map[r[field]] || unc).items.push(r));
  return [...groups.filter((g) => g.items.length), ...(unc.items.length ? [unc] : [])];
}

export function useRecipeFilter(items) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((r) => (!type || r.type_id === type) && (!s || r.name.toLowerCase().includes(s) || (r.ingredients || []).some((i) => (i.name || "").toLowerCase().includes(s))));
  }, [items, q, type]);
  return { q, setQ, type, setType, filtered };
}

export function RecipeFilters({ f, tax, by, setBy, status, setStatus, testid = "recipe-filters" }) {
  return (
    <div className="space-y-2 mb-4" data-testid={testid}>
      <div className="relative"><Search className="h-4 w-4 absolute left-3 top-3.5 text-stone-400" />
        <input className={`${inp} w-full pl-9 h-12 text-base`} placeholder="Search recipes or ingredients" value={f.q} onChange={(e) => f.setQ(e.target.value)} data-testid={`${testid}-search`} /></div>
      <div className="flex flex-wrap gap-2">
        <select className={inp} value={f.type} onChange={(e) => f.setType(e.target.value)} data-testid={`${testid}-type`}>
          <option value="">All recipe types</option>{(tax?.types || []).map((t) => <option key={t.id} value={t.id}>{`${t.name}${t.archived ? " (archived)" : ""}`}</option>)}</select>
        {setBy && <select className={inp} value={by} onChange={(e) => setBy(e.target.value)} data-testid={`${testid}-group-by`}>
          <option value="recipe_categories">Group by recipe category</option><option value="menu_categories">Group by menu category</option><option value="types">Group by recipe type</option></select>}
        {setStatus && <select className={inp} value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`${testid}-status`}>
          <option value="active">Active</option><option value="archived">Archived</option><option value="all">Active & archived</option></select>}
      </div>
    </div>
  );
}

export function CategoryGroups({ groups, render, testid = "recipe-groups" }) {
  const [closed, setClosed] = useState({});
  if (!groups.length) return <p className="text-sm text-stone-500 card-surface p-4" data-testid={`${testid}-empty`}>No recipes match.</p>;
  return (
    <div className="space-y-3" data-testid={testid}>{groups.map((g) => (
      <section key={g.id} className="card-surface overflow-hidden" data-testid={`recipe-group-${g.id}`}>
        <button type="button" className="w-full flex items-center justify-between px-4 py-3 bg-[#F3EFE6]/60 text-left" onClick={() => setClosed({ ...closed, [g.id]: !closed[g.id] })} aria-expanded={!closed[g.id]} data-testid={`recipe-group-toggle-${g.id}`}>
          <span className={`font-medium text-sm ${g.id === "uncategorized" ? "text-amber-800" : ""}`}>{g.name}{g.id === "uncategorized" ? " — needs assignment" : ""}</span>
          <span className="text-xs text-stone-500 flex items-center gap-1">{g.items.length}<ChevronDown className={`h-4 w-4 transition-transform ${closed[g.id] ? "-rotate-90" : ""}`} /></span>
        </button>
        {!closed[g.id] && <div className="divide-y divide-[#F3EFE6]">{g.items.map(render)}</div>}
      </section>))}</div>
  );
}

export function TaxonomyLink() {
  return <Link to="/app/settings/recipe-categories" className="text-sm underline" data-testid="manage-recipe-categories">Manage types & categories</Link>;
}
